import { useEffect, useMemo, useRef, useState } from 'react'

import { DropdownProps } from './types'

// A form field's "nothing picked yet" default is usually `''`, not `null` — so
// an empty string must read as no selection. Otherwise the trigger renders
// `labelRenderer('')` (a raw key, a blank chip, whatever the consumer's mapping
// yields for an absent value) and offers a clear button on an untouched field.
export const hasDropdownValue = <T>(value: T | null | undefined): value is T =>
    value !== null && value !== undefined && (value as unknown) !== ''

type UseDropdownArgs<T> = Pick<
    DropdownProps<T>,
    'items' | 'value' | 'labelRenderer' | 'onChange' | 'onSearch'
>

export function useDropdown<T>({
    items = [],
    value,
    labelRenderer,
    onChange,
    onSearch,
}: UseDropdownArgs<T>) {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [triggerWidth, setTriggerWidth] = useState<number>()
    const [asyncItems, setAsyncItems] = useState<T[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)

    // Latest-callback ref: consumers may pass an inline `onSearch`, which must
    // not re-trigger the debounce — searches key on `query` only.
    const onSearchRef = useRef(onSearch)
    onSearchRef.current = onSearch
    const hasSearch = !!onSearch

    useEffect(() => {
        if (!hasSearch) {
            return
        }
        const trimmed = query.trim()
        if (!trimmed) {
            setAsyncItems([])
            setIsLoading(false)
            setIsError(false)
            return
        }
        setIsLoading(true)
        setIsError(false)
        let cancelled = false
        const timer = setTimeout(() => {
            onSearchRef
                .current?.(trimmed)
                .then(results => {
                    if (!cancelled) {
                        setAsyncItems(results)
                        setIsLoading(false)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setAsyncItems([])
                        setIsError(true)
                        setIsLoading(false)
                    }
                })
        }, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [query, hasSearch])

    const filtered = useMemo(() => {
        if (onSearch) {
            return asyncItems
        }
        const q = query.trim().toLowerCase()
        return q ? items.filter(item => labelRenderer(item).toLowerCase().includes(q)) : items
    }, [onSearch, asyncItems, query, items, labelRenderer])

    const selectedLabel = hasDropdownValue(value) ? labelRenderer(value) : undefined

    const close = () => {
        setIsOpen(false)
        setQuery('')
    }

    const select = (item: T) => {
        onChange(item)
        close()
    }

    const clear = () => {
        onChange(null)
        close()
    }

    const fieldValue = isOpen ? query : (selectedLabel ?? '')

    return {
        isOpen,
        setIsOpen,
        setQuery,
        triggerWidth,
        setTriggerWidth,
        filtered,
        selectedLabel,
        fieldValue,
        isLoading,
        isError,
        close,
        select,
        clear,
    }
}
